<template>
    <div class="mt-5 w-full max-w-7xl">
        <!-- Wallet header with arrows and dropdown -->
        <div class="w-full mt-5">
            <div class="w-full flex items-center gap-3">
                <!-- Left arrow box -->
                <button type="button"
                    :class="['w-[40px] h-[124px] bg-white border border-content-basic-4 rounded-02 flex items-center justify-center shrink-0', canPrev ? 'hover:bg-gray-50' : 'opacity-50 cursor-not-allowed']"
                    :disabled="!canPrev"
                    @click="prevWallet">
                    <ChevronLeftIcon :width="36" :height="36" color="text-content-basic-2" weight="REGULAR" />
                </button>

                <!-- Blue wallet bar (center) -->
                <div class="relative flex-1 w-[936px] h-[124px] bg-primary-3 rounded-02 flex items-center justify-center px-6">
                    <div class="flex flex-col items-center">
                        <div class="relative">
                            <button type="button"
                                class="text-white font-inter-heading-14 flex items-center gap-2" 
                                @click="toggleWalletDropdown">
                                    {{ selectedWallet.walletCategoryName }}
                                <span class="inline-flex items-center justify-center w-[24px] h-[24px] rounded-full bg-[#364EBE]">
                                    <ArrowDropDownIcon
                                        :width="16"
                                        :height="16"
                                        color="text-white"
                                        weight="REGULAR"
                                        :class="[showWalletDropdown ? 'rotate-180' : '', 'transition-transform', 'block']" />
                                </span>
                            </button>
                            <div v-if="showWalletDropdown" class="absolute left-1/2 -translate-x-1/2 top-full mt-2 z-20 w-72">
                                <div class="absolute left-0 top-0 w-full max-h-60 overflow-y-auto bg-background-basic-1 border border-border-basic-2 rounded-01_5 shadow ds-scrollbar">
                                    <button
                                        v-for="w in wallets"
                                        :key="w.walletCategorySeq"
                                        type="button"
                                        class="w-full text-left"
                                        @click="selectWallet(w.walletCategorySeq)"
                                    >
                                        <div class="px-3 py-3 hover:bg-background-basic-2">
                                            <span class="font-inter-heading-20 text-content-basic-1">{{ w.walletCategoryName }}</span>
                                        </div>
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div class="text-white font-inter-heading-1 mt-2">{{ numberWithCommas(selectedWallet.subAdminAvailableMileage) }} Points</div>
                    </div>
                </div>

                <!-- Right arrow box -->
                <button type="button"
                    :class="['w-[40px] h-[124px] bg-white border border-content-basic-4 rounded-02 flex items-center justify-center shrink-0', canNext ? 'hover:bg-gray-50' : 'opacity-50 cursor-not-allowed']"
                    :disabled="!canNext"
                    @click="nextWallet">
                    <ChevronRightIcon :width="36" :height="36" color="text-content-basic-2" weight="REGULAR" />
                </button>
            </div>
        </div>

        <div class="mt-5 flex flex-col bg-white border border-content-basic-4 rounded-02">
            <div class="px-10 py-5 gap-10 flex flex-col">
                <div class="flex flex-col gap-2">
                    <div class="mt-5 font-inter-heading-15 text-content-basic-1 mb-2">Recipient Employees</div>
                    <div class="flex items-center gap-5">
                            <DesignDropdownSelect
                                v-model="searchJsonQuery.departmentSeq" 
                                :options="departmentOptions" 
                                size="LG"
                                :width="'w-60'" 
                                placeholder="All Departments" 
                                :useDefaultMsg="true" 
                                @change="searchEmployeeData" />
                            <DesignDropdownSelect
                                v-model="searchJsonQuery.jobTitleSeq" 
                                :options="jobTitleOptions" 
                                size="LG"
                                :width="'w-60'"
                                placeholder="All Tiers" 
                                :useDefaultMsg="true" 
                                @change="searchEmployeeData" />
                            <ElementsSearchBar
                                v-model="searchInputStr"
                                :options="employeeOptions"
                                :height12="true"
                                :width60="true"
                                :placeholder="employeePlaceholer"
                                @searchEvent="searchEmployeeData"
                                ref="employeeSearchBar"
                                :optionClickEvent="true"
                                :hasSearchButton="true" />
                    </div>
                </div>
            </div>

            <div class="grid gap-4 px-10" style="grid-template-columns: 42% 3.5% 52%;">
            <div>
                <div class="w-full py-10">
                    <div class="w-full h-[1px] bg-[#D9D9D9] relative">
                        <div class="px-5 font-inter-heading-15 text-content-basic-1 bg-[#fff] absolute text-center left-[50%] translate-x-[-50%] top-[-15px] truncate">
                            Choose employee
                        </div>
                    </div>
                </div>
                <div class="h-[46.75rem] border-[#D6DDEB] border-[1px] rounded-lg overflow-hidden">
                    <div class="h-[4.25rem] flex justify-between items-center bg-[#F8F8F8] border-b-[1px]">
                        <h3 class="px-4 text-base font-medium text-slate-500 w-[3.125rem] text-center">No</h3>
                        <h3 class="py-4 text-base font-medium text-slate-500 w-[12.8125rem] text-center">Name</h3>
                        <h3 class="py-4 text-base font-medium text-slate-500 w-[12.8125rem] text-center">
                            Dept. <br />
                            & Tier
                        </h3>
                    </div>

                    <div class="overflow-auto max-h-[42.5rem]">
                        <div class="min-w-full">
                            <template v-for="(employee, index) in employees" v-bind:key="index">
                                <div class="flex justify-between items-center py-2.5"
                                    :class="{ 'bg-slate-50': index % 2 !== 0 }"
                                    @click="clickAddButton(employee)">
                                    <div class="w-[3.125rem] text-sm font-normal text-center truncate">
                                        {{ index + 1 + AccEmployeeCount }}
                                    </div>

                                    <div class="w-[12.8125rem] flex items-center gap-2 px-4">
                                        <StatusCircle :status-code="employee.employeeStatus" class="mt-1" />
                                        <div class="ml-1 flex-1 text-base font-bold line-clamp-2">
                                            {{ employee.employeeName }}
                                        </div>
                                    </div>

                                    <div class="w-[12.8125rem] text-sm font-normal text-center px-4">
                                        <div class="flex-1 text-base">
                                            <p class="font-bold line-clamp-2">
                                                {{ employee.departmentName }}
                                            </p>
                                            <p class="truncate">
                                                {{ employee.jobTitleName }}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>

                <div class="w-full h-28 flex justify-center items-center">
                    <ElementsPagination v-model="currentPage" :totalContent="employeeCount" :contentsPerPage="searchJsonQuery.limit" @clickPage="afterClickPage" />
                </div>
            </div>

            <div class="flex justify-center items-center">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M12.4142 12L11.7071 11.2929L5.70711 5.29289C5.31658 4.90237 4.68342 4.90237 4.29289 5.29289C3.90237 5.68342 3.90237 6.31658 4.29289 6.70711L9.58579 12L4.29289 17.2929C3.90237 17.6834 3.90237 18.3166 4.29289 18.7071C4.68342 19.0976 5.31658 19.0976 5.70711 18.7071L11.7071 12.7071L12.4142 12Z"
                        fill="#393C47" />
                    <path d="M13 6L19 12L13 18" stroke="#393C47" stroke-width="2" stroke-linecap="round" />
                </svg>
            </div>

            <div>
                <div class="w-full py-10">
                    <div class="w-full h-[1px] bg-[#D9D9D9] relative">
                        <div class="px-5 font-inter-heading-15 text-content-basic-1 bg-[#fff] absolute text-center left-[50%] translate-x-[-50%] top-[-15px] truncate">
                            <template v-if="isTransfer"> Recipient List </template>
                            <template v-if="isDeduct"> Recipient List </template>
                        </div>
                    </div>
                </div>
                <div class="h-[46.75rem] border-[#D6DDEB] border-[1px] rounded-lg overflow-hidden">
                    <div class="h-[4.25rem] flex justify-between items-center bg-[#F8F8F8] border-b-[1px]">
                        <h3 class="px-4 text-base font-medium text-slate-500 w-[3.125rem] text-center">No</h3>
                        <h3 class="py-4 text-base font-medium text-slate-500 w-[7.8125rem] text-center">Name</h3>
                        <h3 class="py-4 text-base font-medium text-slate-500 w-[7.8125rem] text-center">
                            Dept. <br />
                            & Tier
                        </h3>
                        <h3 class="py-4 text-base font-medium text-slate-500 w-[7.8125rem] text-center">Point amount*</h3>
                        <h3 class="py-4 text-base font-medium text-slate-500 w-[7.8125rem] text-center">
                            Remark<span class="text-slate-500 font-semibold"><br> (max 20)</span>
                        </h3>
                        <h3 class="py-4 text-base font-medium text-slate-500 w-[1.875rem] text-center"></h3>
                    </div>
                    <div class="overflow-auto max-h-[42.5rem]">
                        <div class="min-w-full">
                            <template v-for="(employee, index) in selectedEmployees" v-bind:key="index">
                                <div class="flex justify-between items-center py-2.5 w-full" :class="{ 'bg-slate-50': index % 2 !== 0 }">
                                    <div class="w-[3.125rem] text-sm font-normal text-center truncate">
                                        {{ index + 1 }}
                                    </div>
                                    <div class="w-[7.8125rem] flex items-center gap-2 px-4">
                                        <StatusCircle :status-code="employee.employeeStatus" />
                                        <div class="ml-1 flex-1 text-base font-bold line-clamp-2">
                                            {{ employee.employeeName }}
                                        </div>
                                    </div>
                                    <div class="w-[7.8125rem] text-sm font-normal text-center ">
                                        <div class="flex-1 text-base">
                                            <p class="font-bold line-clamp-2">
                                                {{ employee.departmentName }}
                                            </p>
                                            <p class="truncate">
                                                {{ employee.jobTitleName }}
                                            </p>
                                        </div>
                                    </div>
                                    <div class="w-[7.8125rem] px-2 text-sm font-normal text-center truncate">
                                        <ElementsInput v-model="employee.mileageVolumeFormat" placeholder="0" :full="true" @input="onInputMileage($event, employee)" />
                                    </div>
                                    <div class="w-[7.8125rem] px-2 text-sm font-normal text-center truncate">
                                        <ElementsInput v-model="employee.transactionNote" placeholder="Remark" :full="true" :maxlength="20" />
                                    </div>
                                    <button @click="clickDeleteButton(index)" class="w-[1.875rem] flex justify-center items-center">
                                        <svg width="12" height="12" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg" class="mr-4">
                                            <path d="M1.66003 8.00016L8.00003 1.66016" stroke="#E73737" stroke-width="1.68021" stroke-linecap="round" stroke-linejoin="round" />
                                            <path d="M8.00003 8.00016L1.66003 1.66016" stroke="#E73737" stroke-width="1.68021" stroke-linecap="round" stroke-linejoin="round" />
                                        </svg>
                                    </button>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </div>

            <div class="w-full px-10 py-5">
                <div class="flex flex-col gap-2">
                    <div class="font-inter-heading-15 text-content-basic-1 mb-2">Message</div>
                    <textarea
                        v-model="transactionMessage"
                        placeholder="Message"
                        maxlength="150"
                        @input="onMessageInput($event.target.value)"
                        :class="['p-5 outline-0 w-full h-[114px] text-sm rounded-lg bg-white transition-all duration-500 shadow shadow-gray-100', showTransactionMessageError ? 'border-accent-red-3 border' : 'border']" />
                    <p v-if="showTransactionMessageError" class="mt-2 font-inter-caption-2 text-accent-red-3">{{ transactionMessageErrorMessage }}</p>
                </div>
            </div>
            <div class="w-full px-10 py-5">
                <div class="font-inter-heading-15 text-content-basic-1">Total</div>
                <div class="mt-4 flex items-baseline gap-16">
                    <div class="flex items-baseline gap-3">
                        <span class="font-inter-heading-19 text-content-basic-1">Employees</span>
                        <span class="font-inter-heading-19 text-primary-3">{{ selectedEmployees.length || '-' }}</span>
                    </div>
                    <div class="flex items-baseline gap-3">
                        <span class="font-inter-heading-19 text-content-basic-1">Total Points</span>
                        <span class="font-inter-heading-19 text-primary-3">{{ totalAmount > 0 ? numberWithCommas(totalAmount) : '-' }}</span>
                    </div>
                </div>

                <button @click="clickTransferButton" v-if="isTransfer" class="rounded-[12px] mt-8 w-full text-center text-[#fff] h-[62px] leading-[62px] bg-[#4640DE]">
                    Transfer
                </button>
                <button @click="clickDeductButton" v-if="isDeduct" class="rounded-[12px] mt-8 w-full text-center text-[#fff] h-[62px] leading-[62px] bg-[#4640DE]">
                    Deduct
                </button>
            </div>
        </div>
        </div>

    <Teleport to="body">
        <DesignConfirmPopup
            v-model="showAlertPopup"
            :showTitle="true"
            title="Failed"
            :confirmDescription="alertMessage"
            buttonText="OK"
            confirmBtnType="RED_SOLID"
            :showCancel="false"
        />
        <DesignConfirmPopup
            v-model="showConfirmPopup"
            :showTitle="true"
            title="Confirm"
            :confirmDescription="confirmMessage"
            buttonText="Confirm"
            :showCancel="true"
            cancelText="Cancel"
            :hasAfterConfirm="true"
            @after-confirm="clickSubmit"
        />
    </Teleport>
</template>

<script>
import ElementsPagination from '@/components/elements/ElementsPagination.vue';
import DesignDropdownSelect from '@/components/design-system/design-dropdown-select.vue';
import DesignConfirmPopup from '@/components/design-system/design-confirm-popup.vue';
import StatusCircle from '@/components/employee/status-circle/status-circle';
import ChevronLeftIcon from '@/components/design-system/icon/chevron-left.vue';
import ChevronRightIcon from '@/components/design-system/icon/chevron-right.vue';
import ArrowDropDownIcon from '@/components/design-system/icon/arrow-drop-down.vue';

export default {
    components: {
        ElementsPagination,
        DesignDropdownSelect,
        DesignConfirmPopup,
        StatusCircle,
        ChevronLeftIcon,
        ChevronRightIcon,
        ArrowDropDownIcon,
    },
    props: {
        transactionType: String,
        // 세금 적용 여부 (Shell 전용)
        taxApplied: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['clickTransferButton', 'clickDeductButton'],
    data() {
        return {
            // Wallet header (aligned with TransferDeductSubAdminToDepartment.vue)
            wallets: [],
            walletIndex: 0,
            selectedWallet: {
                walletCategorySeq: null,
                walletCategoryName: null,
                subAdminAvailableMileage: 0,
            },
            showWalletDropdown: false,

            employees: [],
            selectedEmployees: [],

            currentPage: null,
            employeeCount: null,
            AccEmployeeCount: null,

            searchedText: null,
            searchedValues: null,

            searchJsonQuery: {
                limit: 10,
                offset: null,
                employeeSeqList: null,
                departmentSeq: null,
                jobTitleSeq: null,
            },

            searchInputStr: null,
            employeeOptions: [],
            departmentOptions: [],
            jobTitleOptions: [],
            employeePlaceholer: 'Search Employees',

            totalAmount: 0,
            transactionMessage: '',
            showTransactionMessageError: false,
            transactionMessageErrorMessage: 'This field is required.',

            alertMessage: '',
            confirmMessage: '',
            showAlertPopup: false,
            showConfirmPopup: false,

            scrollYBackup: 0,
        };
    },
    computed: {
        canPrev() {
            return (this.wallets?.length || 0) > 0 && this.walletIndex > 0;
        },
        canNext() {
            return (this.wallets?.length || 0) > 0 && this.walletIndex < (this.wallets.length - 1);
        },
        // 여기에 모달이 더 있으면 || 로 추가
        isAnyPopupOpen() {
            return this.showAlertPopup || this.showConfirmPopup;
        },
        isTransfer() { return this.transactionType === 'transfer'; },
        isDeduct() { return this.transactionType === 'deduct'; },
    },
    watch: {
        isAnyPopupOpen(val) {
            if (val) {
                this.lockBodyScroll();
            } else {
                this.unlockBodyScroll();
            }
        },
    },
    mounted() {
        // Load sub-admin wallets and set default selection
        this.getWallets().then(() => {
            if (!this.selectedWallet?.walletCategorySeq && this.wallets.length > 0) {
                this.selectWallet(this.wallets[0].walletCategorySeq);
            }
        });
        this.getEmployeesListData(true);
        this.getEmployeeOptions();
        this.getDepartmentOptions();
        this.getJobTitleOptions();
    },
    methods: {
        // Wallet header behaviors (aligned with department version)
        toggleWalletDropdown() { this.showWalletDropdown = !this.showWalletDropdown; },
        prevWallet() {
            if (this.wallets.length === 0 || this.walletIndex <= 0) return;
            this.walletIndex = this.walletIndex - 1;
            this.selectWallet(this.wallets[this.walletIndex].walletCategorySeq);
        },
        nextWallet() {
            if (this.wallets.length === 0 || this.walletIndex >= this.wallets.length - 1) return;
            this.walletIndex = this.walletIndex + 1;
            this.selectWallet(this.wallets[this.walletIndex].walletCategorySeq);
        },
        selectWallet(seq) {
            const list = this.wallets || [];
            const idx = list.findIndex(o => o?.walletCategorySeq === seq);
            const found = idx >= 0 ? list[idx] : null;
            this.selectedWallet = {
                walletCategorySeq: found?.walletCategorySeq || null,
                walletCategoryName: found?.walletCategoryName || null,
                subAdminAvailableMileage: Number(found?.walletSubAdminMileage) || 0,
            };
            if (idx >= 0) this.walletIndex = idx;
            this.showWalletDropdown = false;
        },
        getWallets() {
            const url = this.$api('uri', 'get-sub-admin-wallets');
            return this.$axios.get(url).then(res => {
                this.wallets = res.data?.data?.list || [];

                // 첫 번째 항목 기본 선택
                if (this.wallets && this.wallets.length > 0) {
                    this.selectWallet(this.wallets[0].walletCategorySeq);
                }
            }).catch(err => console.error('get Wallets error', err));
        },
        handleTypeChange() {
            // 타입 변경 시 선택 목록/합계 초기화 후 재조회
            this.getWallets();
            this.selectedEmployees = [];
            this.totalAmount = 0;
            this.AccEmployeeCount = 0;
            this.currentPage = 1;
            this.transactionMessage = '';
            this.searchJsonQuery = {
                limit: 10,
                offset: null,
                employeeSeqList: null,
                departmentSeq: null,
                jobTitleSeq: null,
            },
            this.initValidationErrors();
            this.getEmployeeOptions();
            this.getEmployeesListData(true, 0);
        },
        initValidationErrors() {
            this.showTransactionMessageError = false;
            this.showWalletCategoryError = false;
            this.showTransactionMessageError = false;
        },
        lockBodyScroll() {
            // 현재 스크롤 위치 저장
            this.scrollYBackup = window.scrollY || document.documentElement.scrollTop || 0;

            // 레이아웃 점프 방지 및 스크롤 잠금
            document.body.style.position = 'fixed';
            document.body.style.top = `-${this.scrollYBackup}px`;
            document.body.style.left = '0';
            document.body.style.right = '0';
            document.body.style.width = '100%';
            document.body.style.overflow = 'hidden'; // 보조(데스크톱)
        },
        unlockBodyScroll() {
            // 원복
            const y = this.scrollYBackup || 0;
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.left = '';
            document.body.style.right = '';
            document.body.style.width = '';
            document.body.style.overflow = '';

            // 저장해 둔 위치로 복귀
            window.scrollTo(0, y);
        },
        over20Charaters(word) {
            if (!word) return word;

            if (word.length < 21) return word;
            return word.substring(0, 20) + '...';
        },
        getWalletCategoryList() {
            const self = this;
            const url = self.$api('uri', 'get-wallet-category-list');
            self.$axios
                .get(url)
                .then((res) => {
                    self.walletCategoryOptions = res.data.data.list.map((wallet) => ({ text: wallet.walletCategoryName, value: wallet.walletCategorySeq }));
                })
                .catch((err) => {
                    console.error('err : ', err);
                });
        },
        getAvailablePoint() {
            const url = this.$api('uri', 'get-availablePoint');
            this.$axios
                .get(url)
                .then((res) => {
                    this.availableWalletAmount = res.data.data.availableWalletAmount;
                })
                .catch((err) => {
                    console.error('err : ', err);
                });
        },
        onWalletCategoryChange() {},
        handleWalletCategoryChange() {},
        getDepartmentOptions() {
            const url = this.$api('uri', 'get-sub-admin-departments');
            const isTransfer = this.transactionType === 'transfer';
            const params = isTransfer
                ? { includeResigned: false}
                : { includeResigned: true};
            this.$axios.get(url, { params })
            .then(res => {
                this.departments = res.data?.data?.list || [];
                this.departmentOptions = this.departments.map(dep => ({
                    text: dep.departmentName,
                    value: dep.departmentSeq,
                }));
            })
            .catch(err => console.error('getDepartments error', err));
        },
        getJobTitleOptions() {
            const json_query = JSON.stringify({
                limit: -1,
                offset: 0,
            });
            const url = this.$api('uri', 'get-jobTitle');
            this.$axios.get(url, { params: { json_query } }).then((res) => {
                const options = res.data.data.list.map((title) => ({ text: title.jobTitleName, value: title.jobTitleSeq }));
                this.jobTitleOptions.push(...options);
            });
        },
        getEmployeeOptions() {
            const url = this.$api('uri', 'get-sub-admin-employees');
            // Option dropdown DTO: only limit/offset for full list
            const json_query = JSON.stringify({
                limit: -1, 
                offset: 0, 
                includeResigned: this.isDeduct === true
            });

            this.$axios
                .get(url, { params: { json_query } })
                .then((res) => {
                    const list = res?.data?.data?.list || [];
                    this.employeeOptions = list.map((emp) => ({ text: emp.employeeName, value: emp.employeeSeq }));
                })
                .catch((err) => {
                    console.error('getEmployeeOptions error : ', err);
                });
        },
        getEmployeesListData(isInit = false, offset = 0) {
            this.searchJsonQuery.offset = offset;

            if (offset == 0) {
                this.searchedText = this.$refs.employeeSearchBar.getSearchedText();
                this.searchedValues = this.$refs.employeeSearchBar.getSearchedValues();
            } else {
                this.$refs.employeeSearchBar.setInputText(this.searchedText);
            }

            this.searchJsonQuery.employeeSeqList = this.searchedValues;

            const url = this.$api('uri', 'get-sub-admin-employees');
            const json_query = JSON.stringify(this.searchJsonQuery);
            this.$axios
                .get(url, { params: { json_query } })
                .then((res) => {
                    this.employees = res.data.data.list;
                    this.employeeCount = res.data.data.total;

                    // this.currentPage = 1;
                    // if(isInit) this.tabs[0].count = res.data.data.total;
                })
                .catch((err) => {
                    console.error('err : ', err);
                });
        },
        afterClickPage(item) {
            //for employee no 누적
            this.AccEmployeeCount = item;
            this.getEmployeesListData(false, item);
        },
        searchEmployeeData() {
            this.AccEmployeeCount = 0;
            this.getEmployeesListData();
            this.currentPage = 1;
        },
        clickAddButton(selectEmp) {
            //중복일경우
            if (this.selectedEmployees.some((emp) => emp.employeeSeq == selectEmp.employeeSeq)) return;

            //10명이상을 선택할 경우
            if (this.selectedEmployees.length >= 10) {
                
                this.openAlertPopup('You can only select up to 10 people.');
                return;
            }

            this.selectedEmployees.push(selectEmp);
        },
        clickDeleteButton(index) {
            this.selectedEmployees.splice(index, 1);
            this.calTotalAmount();
        },
        clickSubmit() {
            this.hideConfirmPopup();

            if (this.isTransfer) {
                this.submitTransfer();
            } else if (this.isDeduct) {
                this.submitDeduct();
            }
        },
        clickTransferButton() {
            if (this.checkValidation() != 0) {
                return;
            }

            // total amount 가 선택 지갑 가용 포인트보다 큰 경우
            if (this.selectedWallet?.subAdminAvailableMileage != null && this.totalAmount > this.selectedWallet.subAdminAvailableMileage) {
                this.openAlertPopup('The amount of transfer is more than total');
                return;
            }

            this.openConfirmPopup('Are you sure you want to transfer points?');
        },
        submitTransfer() {
            const employeeTransactionForms = this.selectedEmployees.map(emp => ({
                employeeSeq: emp.employeeSeq,
                employeeName: emp.employeeName,
                employeeEmail: emp.email,
                mileageVolume: Number(emp.mileageVolume || 0),
                transactionNote: (emp.transactionNote || '').trim(),
            }));

            const transferForm = {
                // 서버 DTO: TransferSubAdminToEmployeePersonForm (subAdminSeq는 서버 주입)
                walletCategorySeq: this.selectedWallet.walletCategorySeq,
                walletCategoryName: this.selectedWallet.walletCategoryName,
                totalPersonCount: this.selectedEmployees.length,
                totalMileageVolume: Number(this.totalAmount || 0),
                employeeTransactionForms,
                transactionNote: (this.transactionMessage || '').trim(),
                taxAppliedYN: this.taxApplied? 'Y' : 'N',
            };

            this.$emit('clickTransferButton', transferForm);
        },
        clickDeductButton() {
            this.hideConfirmPopup();

            if (this.checkValidation() != 0) {
                return;
            }

            this.openConfirmPopup('Are you sure you want to deduct points?');
        },
        submitDeduct() {
            const employeeTransactionForms = this.selectedEmployees.map(emp => ({
                employeeSeq: emp.employeeSeq,
                employeeName: emp.employeeName,
                employeeEmail: emp.email,
                mileageVolume: Number(emp.mileageVolume || 0),
                transactionNote: (emp.transactionNote || '').trim(),
            }));

            const deductForm = {
                employeeTransactionForms,
                walletCategorySeq: this.selectedWallet.walletCategorySeq,
                walletCategoryName: this.selectedWallet.walletCategoryName,
                totalPersonCount: this.selectedEmployees.length,
                totalMileageVolume: this.totalAmount,
                transactionNote: (this.transactionMessage || '').trim(),
            };

            this.$emit('clickDeductButton', deductForm);
        },
        onInputMileage(event, employee) {
            const input = event.target.value;
            // 숫자만 추출하고, 앞자리 0 제거
            const mileage = input.replace(/[^0-9]/g, '').replace(/^0+(?!$)/, '');

            employee.mileageVolume = mileage;
            employee.mileageVolumeFormat = this.numberWithCommas(mileage);

            this.calTotalAmount();
        },
        onMessageInput(val) {
            // validation을 한번도 실행하지 않은 경우에는 Message 필수입력 오류 메세지 보여지지 않도록 함
            if (!this.hasRunValidation) {
                return;
            }
            this.showTransactionMessageError = !String(val).trim();
        },
        calTotalAmount() {
            if (this.selectedEmployees.length == 0) {
                this.totalAmount = 0;
                return this.totalAmount;
            }
            let sum = 0;
            this.selectedEmployees.forEach((employee) => {
                const points = Number(employee.mileageVolume);
                sum += points;
            });
            this.totalAmount = sum;
            return this.totalAmount;
        },
        checkValidation() {
            this.hasRunValidation = true;
            this.initValidationErrors();
            
            if ((this.transactionMessage || '').trim().length === 0) {
                this.showTransactionMessageError = true;
            }

            const hasWallet = !!(this.selectedWallet && this.selectedWallet.walletCategorySeq);

            if (this.isTransfer && (this.showTransactionMessageError || !hasWallet)) {
                this.openAlertPopup('Transfer failed due to an invalid input. Please check and try again.');
                return 1;
            }

            if (this.isDeduct && (this.showTransactionMessageError || !hasWallet)) {
                this.openAlertPopup('Deduct failed due to an invalid input. Please check and try again.');
                return 1;
            }

            //validation ---------------------------------------------------------------------------------
            //중복 값 제거
            let validationCnt = 0;
            this.selectedEmployees = this.selectedEmployees.filter((character, idx) => {
                if (character.email == null || '') return;
                return this.selectedEmployees.findIndex((item) => item.email == character.email) === idx;
            });

            // mileageVolume 에 정수 외의 값을 입력했을 경우
            this.selectedEmployees.forEach((employee) => {
                const points = Number(employee.mileageVolume);
                if (Number.isNaN(points) || points < 1) {
                    validationCnt++;
                }
            });

            // 세금 적용 시, 각 직원별 전송 포인트가 최소 2 이상인지 확인
            if (this.isTransfer && this.taxApplied) {
                const hasBelowMinimum = this.selectedEmployees.some((employee) => {
                    const amount = Number(employee.mileageVolume || 0);
                    return Number.isNaN(amount) || amount < 2;
                });

                if (hasBelowMinimum) {
                    this.openAlertPopup('Please enter at least 2 points for each employee.');
                    return 1;
                }
            }

            if (validationCnt > 0) {
                this.openAlertPopup('You need to enter the point amount.');
                return validationCnt++;
            }

            //아무도 선택하지 않았을 경우
            if (this.selectedEmployees.length == 0) {
                this.openAlertPopup('Please select at least one employee.');
                validationCnt++;
            }

            if (validationCnt > 0) return validationCnt;

            this.calTotalAmount();
            return validationCnt;
        },
        numberWithCommas(number) {
            if (!number) {
                return 0;
            }
            return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        },
        openAlertPopup(message) {
            this.alertMessage = message;
            this.showAlertPopup = true;
        },
        hideAlertPopup() {
            this.showAlertPopup = false;
        },
        openConfirmPopup(message) {
            this.confirmMessage = message;
            this.showConfirmPopup = true;
        },
        hideConfirmPopup() {
            this.showConfirmPopup = false;
        },
        resetState() {
            // reset form state
            this.transactionMessage = '';
            this.showTransactionMessageError = false;
            this.selectedEmployees = [];
            this.totalAmount = 0;
            this.currentPage = 1;
            this.searchedText = '';
            this.searchedValues = null;
            this.searchInputStr = '';
            this.searchJsonQuery = {
                limit: 10,
                offset: 0,
                employeeSeqList: null,
                departmentSeq: null,
                jobTitleSeq: null,
            };

            // refresh filters/options and list
            this.getWallets();
            this.getEmployeeOptions();
            this.getDepartmentOptions();
            this.getJobTitleOptions();
            this.getEmployeesListData(true, 0);
        },
    },
};
</script>
